/**
 * Concept Detail Sidebar — ROUTE_INVENTORY §2.10 #34–#45.
 *
 * A right-hand drawer over the Knowledge Archive, opened by selecting a
 * concept and dismissed by the backdrop or the header ✕.
 *
 * Layout (top → bottom):
 *
 *   ┌────────────────────────── close ✕
 *   │ Header strip
 *   ├──────────────────────────
 *   │ [Orphan banner]   — only when the source lesson is gone
 *   │ [Paused banner]   — only when the user paused reviews
 *   │ Hero: title, level / importance / mastery pills, description
 *   │ Know first (prerequisites) + goal / key points / mistakes, from the plan
 *   │ Memory health
 *   │ Tags
 *   │ Sources — lessons + documents
 *   │ Related concepts
 *   └────────────────────────── sticky CTA
 *
 * This file is composition and state only. Every block above lives beside it
 * under `features/mindspace/concepts/` with a story of its own, so what is left
 * here is the orchestration: which branch, with which props, and the two
 * mutations that need a confirm.
 *
 * ## There is no reachable error state
 *
 * `useConceptDetail` catches a failed request and falls back to mock data
 * (§2.10 #35) — the same shape as `useKnowledgeArchiveGraph`. So `error` stays
 * undefined in every data mode and `ConceptDetailError` is rendered here for
 * the day that changes, and storied at component grain where it can be reached.
 *
 * ## Render it outside any transformed or blurred ancestor
 *
 * The scrim and panel are `position: fixed`, and an ancestor with a
 * `transform` (the `animate-fade-in-up` cards) or `backdrop-filter`
 * (`backdrop-blur-*`) becomes their containing block — the scrim then dims
 * only that card. `KnowledgeArchivePage` mounts it as a sibling of
 * `MindspaceLayout` for exactly this reason; keep it there.
 *
 * ## Pausing deliberately leaves the sidebar open
 *
 * Removing a concept closes the panel, because the thing it was describing no
 * longer exists. Pausing does NOT (§2.10 #39): the concept is still there, the
 * user has just changed its state, and closing the panel would hide the paused
 * banner and the Resume button that undoes it — turning a reversible action
 * into one with no visible way back.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, PauseCircle, PlayCircle, X } from 'lucide-react';
import {
  useConceptDetail,
  useDeleteConcept,
  useSetConceptArchived,
} from '@/hooks/mindspace/useMindspace';
import { UserTagEditor } from '@/components/features/mindspace/filters/UserTagEditor';
import { ConfirmDialog } from '@/components/core/common/feedback/ConfirmDialog';
import {
  ConceptDetailEmpty,
  ConceptDetailError,
  ConceptDetailLoading,
} from '@/components/features/mindspace/concepts/ConceptSidebarStates';
import {
  OrphanBanner,
  PausedBanner,
} from '@/components/features/mindspace/concepts/ConceptSidebarBanners';
import { ConceptHeroBlock } from '@/components/features/mindspace/concepts/ConceptHeroBlock';
import { MemoryHealthCard } from '@/components/features/mindspace/concepts/MemoryHealthCard';
import type { MemoryTier } from '@/lib/mindspace/memoryTier';
import { ConceptSourcesSection } from '@/components/features/mindspace/concepts/ConceptSourcesSection';
import { RelatedConceptList } from '@/components/features/mindspace/concepts/RelatedConceptList';
import { ConceptNotes } from '@/components/features/mindspace/concepts/ConceptNotes';
import { PrerequisitesBox } from '@/components/features/mindspace/concepts/PrerequisitesBox';
import type { ConceptDetail } from '@/types/mindspace-concept';

interface ConceptDetailSidebarProps {
  conceptId: string;
  /**
   * Whether the panel dims and seals the page behind it.
   *
   * True on a phone, where it is full-bleed and there is nothing behind it to
   * interact with. **False on desktop**, where the thing behind it is the
   * graph that opened it: clicking a concept lights its links to every other
   * cluster, and a `bg-black/50` scrim put that answer under a 50% wash. The
   * panel sits beside a live canvas instead, so you can read the connections
   * and click the next concept without closing anything.
   *
   * Escape and the ✕ close it either way.
   */
  modal?: boolean;
  /**
   * The graph's own answer to "what is linked to this", which the detail
   * endpoint does not carry — only the page holds every edge. See
   * `RelatedConceptList` for why these are separated by kind.
   */
  comesBefore?: Array<{ id: string; title: string }>;
  sameLessonCount?: number;
  /** The state the map drew this concept in; see `MemoryHealthCard`. */
  mapTier?: MemoryTier;
  onClose: () => void;
  /** Switch the sidebar to a related concept without going back to the graph. */
  onSelectConcept?: (conceptId: string) => void;
}

export function ConceptDetailSidebar({
  conceptId,
  modal = true,
  comesBefore,
  sameLessonCount,
  mapTier,
  onClose,
  onSelectConcept,
}: ConceptDetailSidebarProps) {
  const navigate = useNavigate();
  const { data: concept, isLoading, error } = useConceptDetail(conceptId);
  const deleteMutation = useDeleteConcept();
  const archiveMutation = useSetConceptArchived();

  const isArchived = concept?.is_archived ?? false;

  // Which themed confirm dialog (if any) is open. Replaces the native
  // window.confirm() prompts, which ignored the app theme/tokens.
  const [confirmAction, setConfirmAction] = useState<null | 'remove' | 'pause'>(
    null
  );

  const confirmRemove = () =>
    deleteMutation.mutate(conceptId, { onSuccess: onClose });

  // See the file docstring — the panel stays open on purpose.
  const confirmPause = () =>
    archiveMutation.mutate({ conceptId, archived: true });

  const handleResume = () =>
    archiveMutation.mutate({ conceptId, archived: false });

  // Escape closes the panel - unless one of its confirm dialogs is open,
  // which takes Escape for itself.
  useEffect(() => {
    if (confirmAction) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [confirmAction, onClose]);

  return (
    <>
      {/* The scrim exists to say "nothing behind this is live". On desktop
          something behind it very much is — see the `modal` prop. */}
      {modal && (
        <div
          className='fixed inset-0 z-40 bg-black/50 duration-200 animate-in fade-in'
          onClick={onClose}
          aria-hidden='true'
        />
      )}

      {/* Closed by Escape as well as the X. Full width below `sm`: the 92vw it
          used to take left a sliver of page down the left edge to mis-tap. */}
      <div
        role='dialog'
        aria-modal={modal}
        aria-label='Concept'
        className='fixed right-0 top-0 z-50 flex h-full w-[420px] max-w-[92vw] flex-col overflow-hidden border-l border-border bg-card shadow-2xl duration-300 animate-in slide-in-from-right max-sm:w-full max-sm:max-w-none max-sm:border-l-0'
      >
        {/* Header - h-16 to match the Filters / page header height. */}
        <div className='flex h-16 flex-shrink-0 items-center justify-between border-b border-border bg-card/95 px-5 backdrop-blur'>
          {/* The concept's name, always, on the same row as the close
              button — it is the panel's heading and it should be the first
              thing read and the thing still visible once the body scrolls.
              It used to say "Concept" above a separate heading that said
              "Cross Entropy"; briefly it faded in only on scroll, which left
              the top of the panel anonymous until you moved. The body no
              longer repeats it.

              Truncated rather than wrapped: a two-line header would push the
              content down on every long title, and the full name is in the
              page's own data if it is ever needed. */}
          <h3 className='truncate pr-2 text-base font-semibold text-foreground'>
            {concept?.title ?? 'Concept'}
          </h3>
          <button
            onClick={onClose}
            className='tap-target rounded-md p-1.5 transition-colors hover:bg-muted'
            aria-label='Close sidebar'
          >
            <X className='h-4 w-4' aria-hidden />
          </button>
        </div>

        <div className='flex-1 overflow-y-auto'>
          {isLoading ? (
            <ConceptDetailLoading />
          ) : error ? (
            <ConceptDetailError />
          ) : concept ? (
            <div className='space-y-5 px-5 py-5 pb-2'>
              {concept.is_orphaned && (
                <OrphanBanner
                  onRemove={() => setConfirmAction('remove')}
                  pending={deleteMutation.isPending}
                />
              )}

              {isArchived && (
                <PausedBanner
                  onResume={handleResume}
                  pending={archiveMutation.isPending}
                />
              )}

              {/* Ordered by the questions a reader actually arrives with:
                  what is this → do I still know it → where did it come from →
                  what is it near → and only then the metadata. The tag editor
                  used to sit third, above both provenance and relationships,
                  which put an input box in front of the content. */}
              <ConceptHeroBlock concept={concept} />

              <ConceptPlanBlock
                concept={concept}
                onSelectConcept={onSelectConcept}
              />

              <DividerRule />

              <MemoryHealthCard concept={concept} mapTier={mapTier} />

              <DividerRule />

              <ConceptSourcesSection
                lessons={concept.source_lessons}
                documents={concept.source_documents ?? []}
                onNavigateToLesson={lessonId => {
                  onClose();
                  navigate(`/my-learning/${lessonId}`);
                }}
              />

              <RelatedConceptList
                items={concept.related_concepts}
                comesBefore={comesBefore}
                sameLessonCount={sameLessonCount}
                onSelect={onSelectConcept}
              />

              <DividerRule />

              <section>
                <UserTagEditor
                  conceptId={concept.id}
                  domainTags={concept.domain_tags ?? []}
                  userTags={concept.user_tags ?? []}
                />
              </section>
            </div>
          ) : (
            <ConceptDetailEmpty />
          )}
        </div>

        {/* Sticky CTA footer - always reachable without scrolling. §2.10 #40. */}
        {concept && (
          <div className='flex-shrink-0 space-y-2 border-t border-border bg-card/95 px-5 py-3 backdrop-blur'>
            {isArchived ? (
              // Paused concepts are excluded from the review pool, so
              // "Practice" would be a no-op - offer Resume as the CTA instead.
              <button
                onClick={handleResume}
                disabled={archiveMutation.isPending}
                className='flex w-full items-center justify-center gap-1.5 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground shadow-sm transition-all hover:bg-primary/90 hover:shadow disabled:opacity-60'
              >
                {archiveMutation.isPending ? (
                  <Loader2 className='h-4 w-4 animate-spin' aria-hidden />
                ) : (
                  <PlayCircle className='h-4 w-4' aria-hidden />
                )}
                Resume reviews
              </button>
            ) : (
              <>
                <button
                  onClick={() => {
                    onClose();
                    navigate(`/mindspace/practice-arena?concept=${concept.id}`);
                  }}
                  className='flex w-full items-center justify-center rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground shadow-sm transition-all hover:bg-primary/90 hover:shadow'
                >
                  Practice this concept
                </button>
                <button
                  onClick={() => setConfirmAction('pause')}
                  disabled={archiveMutation.isPending}
                  title='Stop this concept from appearing in reviews. Reversible.'
                  className='flex w-full items-center justify-center gap-1.5 rounded-lg px-4 py-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-60'
                >
                  {archiveMutation.isPending ? (
                    <Loader2 className='h-3.5 w-3.5 animate-spin' aria-hidden />
                  ) : (
                    <PauseCircle className='h-3.5 w-3.5' aria-hidden />
                  )}
                  Pause reviews
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Themed confirm dialogs - replace the old native window.confirm() so
          the prompts respect the app's tokens + light/dark theme. */}
      <ConfirmDialog
        isOpen={confirmAction === 'remove'}
        onClose={() => setConfirmAction(null)}
        onConfirm={confirmRemove}
        title='Remove from mindspace?'
        description="This concept and its review history will be permanently removed from your mindspace. This can't be undone."
        confirmText='Remove'
        variant='danger'
      />
      <ConfirmDialog
        isOpen={confirmAction === 'pause'}
        onClose={() => setConfirmAction(null)}
        onConfirm={confirmPause}
        title='Pause reviews?'
        description='This concept stops appearing in reviews (Practice Arena and Quick Bytes) and drops out of the default graph. Your review history is kept – resume anytime.'
        confirmText='Pause'
        variant='info'
      />
    </>
  );
}

function DividerRule() {
  return <hr className='border-border/60' />;
}

/** What the lesson plan says about the concept: "Know first", then the
 *  goal, key points and mistakes. A matched prerequisite swaps the sidebar to
 *  it, like a related concept. Nothing renders for a concept with none. */
function ConceptPlanBlock({
  concept,
  onSelectConcept,
}: {
  concept: ConceptDetail;
  onSelectConcept?: (conceptId: string) => void;
}) {
  const prerequisites = (concept.prerequisites ?? []).map(p => ({
    name: p.title || p.name,
    onOpen:
      p.concept_id && onSelectConcept
        ? () => onSelectConcept(p.concept_id as string)
        : undefined,
  }));
  return (
    <div className='space-y-4'>
      <PrerequisitesBox items={prerequisites} />
      <ConceptNotes
        learningGoal={concept.learning_goal}
        keyPoints={concept.key_points}
        misconceptions={concept.misconceptions}
      />
    </div>
  );
}
